import { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { api, ChatChannelResponse, ChatMessageResponse } from '@/lib/api';
import { sanitizeChatHtml } from '@/lib/sanitize-chat-html';
import { channelDisplayName } from '../utils';

/** How many recent messages to peek at. A preview, not the full history. */
const PREVIEW_LIMIT = 30;

interface ChannelMessagePreviewProps {
  channel: ChatChannelResponse;
  currentUserId: string | null;
  onClose: () => void;
}

/**
 * Read-only peek at a channel's recent messages, opened by press-and-hold on a
 * channel in the list. It fetches messages directly via `api.listChatMessages`
 * and deliberately does NOT call `markChatChannelRead`, set the active channel,
 * or open the real message thread — so peeking leaves the unread count exactly
 * where it was. That "stays unread" guarantee is the entire point of the
 * feature, which is why this component owns its own message state rather than
 * reusing the `useChat` / `useChatMessages` hooks (both of which mark read).
 */
export function ChannelMessagePreview({
  channel,
  currentUserId,
  onClose,
}: ChannelMessagePreviewProps) {
  const [messages, setMessages] = useState<ChatMessageResponse[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const title = channelDisplayName(channel);

  useEffect(() => {
    let cancelled = false;
    setMessages(null);
    setError(null);
    api
      .listChatMessages(channel.channelId, { limit: PREVIEW_LIMIT })
      .then((res) => {
        if (cancelled) return;
        // The API returns newest-first; show oldest→newest like the real thread.
        setMessages([...res.messages].reverse());
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load messages');
      });
    return () => {
      cancelled = true;
    };
  }, [channel.channelId]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Land on the newest message once loaded, like opening the channel would.
  useEffect(() => {
    if (messages && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview of ${title}`}
    >
      <div
        className="flex max-h-[70vh] w-full max-w-md flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-foreground">{title}</h2>
            <p className="text-[11px] text-muted-foreground">Preview · stays unread</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            className="flex-shrink-0 rounded p-1 text-muted-foreground hover:bg-foreground/10 hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
          {error ? (
            <p className="py-8 text-center text-sm text-destructive">{error}</p>
          ) : !messages ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : messages.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No messages yet.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {messages.map((m) => (
                <PreviewMessage
                  key={m.messageId}
                  message={m}
                  isOwn={!!currentUserId && m.senderId === currentUserId}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewMessage({ message, isOwn }: { message: ChatMessageResponse; isOwn: boolean }) {
  const name = isOwn ? 'You' : message.senderName || 'Unknown';
  const time = new Date(message.createdAt).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const hasText = !!message.content?.trim();
  const sanitized = useMemo(
    () => (hasText ? sanitizeChatHtml(message.content) : ''),
    [hasText, message.content],
  );
  const attachmentCount = message.attachmentRefs?.length ?? 0;

  return (
    <li className="flex flex-col gap-0.5">
      <div className="flex items-baseline gap-2">
        <span className="text-xs font-semibold text-foreground">{name}</span>
        <span className="text-[10px] text-muted-foreground">{time}</span>
      </div>
      {hasText ? (
        <div
          className="chat-message-content break-words text-[13px] leading-[1.4] text-foreground"
          dangerouslySetInnerHTML={{ __html: sanitized }}
        />
      ) : attachmentCount > 0 ? (
        <p className="text-[13px] italic text-muted-foreground">
          📎 {attachmentCount} attachment{attachmentCount === 1 ? '' : 's'}
        </p>
      ) : (
        <p className="text-[13px] italic text-muted-foreground">No content</p>
      )}
    </li>
  );
}
